import { FiEdit3 } from 'react-icons/fi';

export const vocabularyWritingData = {
  courseId: 'vocabulary-writing',
  courseType: 'vocabulary',
  title: 'Vocabulary for Writing',
  icon: FiEdit3,
  overview: {
    impact: 'Boost your Writing band score',
    focus: 'Formal vocabulary for essays',
    uses: 'Writing Task 1 & 2',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Formal Academic Vocabulary',
      content: `<p>IELTS Writing requires formal vocabulary. Avoid informal words:</p>`,
      table: {
        headers: ['Avoid', 'Use Instead'],
        rows: [
          ['thing/stuff', 'factor, aspect, element, issue'],
          ['a lot of', 'numerous, a significant number of, considerable'],
          ['get', 'obtain, acquire, receive, achieve'],
          ['good/bad', 'beneficial/detrimental, positive/negative'],
          ['kind of/sort of', 'somewhat, to some extent, relatively'],
        ],
      },
    },
    {
      title: 'Task 1 Vocabulary',
      content: `<p>Essential words for describing data:</p>`,
      table: {
        headers: ['Category', 'Vocabulary'],
        rows: [
          ['Increase', 'rise, grow, climb, surge, soar, escalate'],
          ['Decrease', 'fall, drop, decline, plummet, decrease'],
          ['Stability', 'remain stable, stay constant, plateau, level off'],
          ['Degree', 'dramatically, significantly, gradually, slightly, marginally'],
          ['Comparison', 'whereas, while, in contrast, compared to, similarly'],
        ],
      },
    },
    {
      title: 'Task 2 Vocabulary',
      content: `<p>Key phrases for essay writing:</p>`,
      points: [
        '<strong>Introducing views:</strong> It is often argued that..., Many people believe...',
        '<strong>Giving opinion:</strong> In my view..., I firmly believe..., From my perspective...',
        '<strong>Adding points:</strong> Furthermore..., Moreover..., In addition...',
        '<strong>Contrasting:</strong> However..., Nevertheless..., On the other hand...',
        '<strong>Concluding:</strong> In conclusion..., To summarize..., Overall...',
      ],
    },
  ],
  exercises: [
    {
      title: 'Make It Formal',
      type: 'select',
      instructions: 'Choose the more formal alternative.',
      questions: [
        {
          text: '"There are lots of problems with this approach."',
          options: [
            'There are numerous problems with this approach.',
            'There are lots of issues with this approach.',
            'There are many probs with this approach.',
          ],
          answer: 'There are numerous problems with this approach.',
          explanation: '"Numerous" is more formal than "lots of".',
        },
        {
          text: '"People need to get better education."',
          options: [
            'Individuals require access to quality education.',
            'People need to get better education.',
            'Folks should get learning.',
          ],
          answer: 'Individuals require access to quality education.',
          explanation: 'Uses formal vocabulary throughout.',
        },
      ],
    },
    {
      title: 'Describe Trends',
      type: 'writing',
      instructions: 'Write a sentence describing each trend.',
      questions: [
        {
          text: 'Sales: 100 → 500 (dramatic increase)',
          example: 'Sales surged dramatically from 100 to 500 over the period.',
          tip: 'Use strong verbs (surged, soared) with degree words (dramatically).',
        },
        {
          text: 'Population: stayed at 1 million for 10 years',
          example: 'The population remained stable at approximately 1 million throughout the decade.',
          tip: 'Use stability vocabulary (remained stable, stayed constant).',
        },
      ],
    },
  ],
  tips: [
    'Replace informal words with formal alternatives',
    'Use topic-specific vocabulary in Task 2',
    'Master trend vocabulary for Task 1',
    'Learn signposting phrases for essay structure',
  ],
  dailyPractice: [
    'Upgrade informal vocabulary in practice essays',
    'Practice Task 1 descriptions with varied vocabulary',
    'Learn new formal synonyms for common words',
    'Read model essays and note vocabulary used',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Vocabulary for Writing: Formal academic vocabulary for IELTS Writing including trend words, signposting phrases, and formal alternatives.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...vocabularyWritingData,
      title: vocabularyWritingData.title + ' (Foundations)',
      overview: {
        ...vocabularyWritingData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: vocabularyWritingData.sections.slice(0, Math.max(2, Math.ceil(vocabularyWritingData.sections.length / 2))),
      exercises: vocabularyWritingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...vocabularyWritingData,
      title: vocabularyWritingData.title + ' (Advanced)',
      overview: {
        ...vocabularyWritingData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: vocabularyWritingData.sections,
      exercises: vocabularyWritingData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(vocabularyWritingData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return vocabularyWritingData;
}
